// Newsletter — footer subscribers roster. Read-mostly; admins flip status
// or delete (GDPR right-to-be-forgotten) and export to CSV for handoff to
// the eventual campaign tool (Mailchimp/Brevo).

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  PageHeader,
  ToolBtn,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  deleteNewsletterSubscriber,
  downloadNewsletterCsv,
  fetchAdminNewsletter,
  importNewsletterCsv,
  setNewsletterStatus,
  type AdminNewsletterListParams,
  type AdminNewsletterRow,
  type NewsletterImportResponse,
  type SubscriberStatus,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminNewsletterListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'subscribed', label: 'Subscribed' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
  { key: 'bounced', label: 'Bounced' },
];

const STATUS_KEYS: SubscriberStatus[] = ['subscribed', 'unsubscribed', 'bounced'];

const fmtTime = (iso: string | null) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

interface ActionCtx {
  busyId: number | null;
  onStatus: (id: number, status: SubscriberStatus) => void;
  onDelete: (row: AdminNewsletterRow) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminNewsletterRow>[] => [
  {
    key: 'email',
    h: 'Email',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.email}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}
        </div>
      </div>
    ),
  },
  { key: 'source', h: 'Source', cell: (r) => r.source ?? '—' },
  {
    key: 'consent',
    h: 'Consent',
    cell: (r) =>
      r.consent ? (
        <span className="text-[12px] text-[#166534]">Yes</span>
      ) : (
        <span className="text-[12px] text-[#9f1239]">No</span>
      ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'createdAt',
    h: 'Joined',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {fmtTime(r.createdAt)}
      </span>
    ),
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busyId === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.status !== 'subscribed' ? (
            <RowAction busy={busy} onClick={() => ctx.onStatus(r.id, 'subscribed')}>
              Resubscribe
            </RowAction>
          ) : null}
          {r.status !== 'unsubscribed' ? (
            <RowAction busy={busy} onClick={() => ctx.onStatus(r.id, 'unsubscribed')}>
              Unsubscribe
            </RowAction>
          ) : null}
          {r.status !== 'bounced' ? (
            <RowAction busy={busy} onClick={() => ctx.onStatus(r.id, 'bounced')}>
              Mark bounced
            </RowAction>
          ) : null}
          <RowAction kind="danger" busy={busy} onClick={() => ctx.onDelete(r)}>
            Delete
          </RowAction>
        </div>
      );
    },
  },
];

export function Newsletter() {
  const [filter, setFilter] = useState<FilterKey>('subscribed');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'newsletter', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminNewsletter(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter'] });
  };

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: SubscriberStatus }) =>
      setNewsletterStatus(id, status),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteNewsletterSubscriber(id),
    onSuccess: invalidate,
  });

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadNewsletterCsv(filter);
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const busyId =
    (statusMut.isPending && statusMut.variables ? statusMut.variables.id : null) ??
    (deleteMut.isPending && deleteMut.variables ? deleteMut.variables : null);

  const cols = buildColumns({
    busyId,
    onStatus: (id, status) => statusMut.mutate({ id, status }),
    onDelete: (row) => {
      if (window.confirm(`Permanently delete ${row.email}? This cannot be undone.`)) {
        deleteMut.mutate(row.id);
      }
    },
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const counts = data?.statusCounts ?? {};
  const mutationError = statusMut.error || deleteMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Commerce"
        title="Newsletter"
        sub={`${total.toLocaleString()} total · ${counts.subscribed ?? 0} subscribed.`}
        actions={
          <>
            <ToolBtn onClick={() => setImportOpen(true)}>Import CSV</ToolBtn>
            <ToolBtn icon="download" onClick={() => void handleExport()}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </ToolBtn>
          </>
        }
      />
      {importOpen ? (
        <ImportModal
          onClose={() => setImportOpen(false)}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin', 'newsletter'] });
          }}
        />
      ) : null}
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load subscribers: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}
      {exportError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Export failed: {exportError}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search email or name…" />
        {FILTERS.map((f) => (
          <ToolChip
            key={f.key}
            active={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key !== 'all' && STATUS_KEYS.includes(f.key as SubscriberStatus) ? (
              <span className="ms-1 tabular-nums text-[10.5px] text-[color:var(--bo-mute)]">
                {counts[f.key] ?? 0}
              </span>
            ) : null}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={cols}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No matching subscribers.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}

// ─── Import modal ────────────────────────────────────────────────────
//
// Two-step flow: paste/pick CSV → Validate (dry-run) → review counts +
// errors → Confirm import. Dry-run avoids accidentally committing a CSV
// with bad rows; operator sees what would land before it does.

function ImportModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [csv, setCsv] = useState('');
  const [stage, setStage] = useState<'compose' | 'preview' | 'done'>('compose');
  const [preview, setPreview] = useState<NewsletterImportResponse | null>(null);
  const [result, setResult] = useState<NewsletterImportResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      setCsv(text);
    };
    reader.readAsText(file);
  };

  const runDry = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await importNewsletterCsv(csv, { dryRun: true });
      setPreview(res);
      setStage('preview');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const runImport = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await importNewsletterCsv(csv, { dryRun: false });
      setResult(res);
      setStage('done');
      onSuccess();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Import newsletter subscribers"
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/30 px-4 py-[8vh]"
      onClick={onClose}
    >
      <div
        className="flex max-h-[84vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-[color:var(--bo-line)] bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-[color:var(--bo-line)] px-5 py-3">
          <div>
            <div className="text-[14px] font-semibold tracking-tight">
              Import subscribers
            </div>
            <div className="text-[11.5px] text-[color:var(--bo-mute)]">
              Paste CSV or upload a file. We&apos;ll validate before writing.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-[color:var(--bo-line)] px-2 py-1 text-[11px] text-[color:var(--bo-mute)] hover:border-[color:var(--bo-mute)]"
          >
            Close
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {stage === 'compose' ? (
            <>
              <p className="mb-3 text-[12.5px] text-[color:var(--bo-mute)]">
                Required header: <code className="font-mono">email</code>. Optional
                headers: <code className="font-mono">first_name</code>,{' '}
                <code className="font-mono">last_name</code>,{' '}
                <code className="font-mono">status</code>{' '}
                <span className="opacity-70">(subscribed/unsubscribed/bounced)</span>,{' '}
                <code className="font-mono">consent</code>,{' '}
                <code className="font-mono">source</code>. Duplicate emails in the
                file are deduped (last wins); existing rows are upserted by email.
              </p>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={(e) => onFile(e.target.files?.[0] ?? null)}
                className="mb-3 block text-[12px]"
              />
              <textarea
                value={csv}
                onChange={(e) => setCsv(e.target.value)}
                placeholder="email,first_name,last_name&#10;alice@example.com,Alice,Smith&#10;bob@example.com,Bob,&#10;…"
                rows={12}
                spellCheck={false}
                className="block w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 font-mono text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </>
          ) : null}

          {stage === 'preview' && preview ? (
            <ImportPreview data={preview} />
          ) : null}

          {stage === 'done' && result ? (
            <ImportResult data={result} />
          ) : null}

          {error ? (
            <div className="mt-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
              {error}
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]/40 px-5 py-3">
          {stage === 'compose' ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[12.5px] font-medium"
              >
                Cancel
              </button>
              <RowAction
                kind="primary"
                busy={submitting}
                disabled={!csv.trim() || submitting}
                onClick={() => void runDry()}
              >
                Validate
              </RowAction>
            </>
          ) : null}
          {stage === 'preview' ? (
            <>
              <button
                type="button"
                onClick={() => setStage('compose')}
                className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[12.5px] font-medium"
              >
                Back
              </button>
              <RowAction
                kind="primary"
                busy={submitting}
                disabled={submitting || !preview?.wouldInsertOrUpdate}
                onClick={() => void runImport()}
              >
                Import {preview?.wouldInsertOrUpdate?.toLocaleString() ?? 0} rows
              </RowAction>
            </>
          ) : null}
          {stage === 'done' ? (
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white"
            >
              Done
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
}

function ImportPreview({ data }: { data: NewsletterImportResponse }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Rows in file" value={data.totalRows.toLocaleString()} />
        <Stat
          label="Would insert/update"
          value={(data.wouldInsertOrUpdate ?? 0).toLocaleString()}
          tone="ok"
        />
        <Stat
          label="Rejected"
          value={data.errors.length.toLocaleString()}
          tone={data.errors.length > 0 ? 'warn' : 'ok'}
        />
      </div>
      {data.errors.length > 0 ? (
        <ErrorList errors={data.errors} />
      ) : (
        <div className="rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] px-3 py-2 text-[12.5px] text-[#065f46]">
          All rows valid — safe to import.
        </div>
      )}
    </div>
  );
}

function ImportResult({ data }: { data: NewsletterImportResponse }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Inserted" value={(data.inserted ?? 0).toLocaleString()} tone="ok" />
        <Stat label="Updated" value={(data.updated ?? 0).toLocaleString()} tone="ok" />
        <Stat
          label="Rejected"
          value={data.errors.length.toLocaleString()}
          tone={data.errors.length > 0 ? 'warn' : 'ok'}
        />
      </div>
      {data.errors.length > 0 ? <ErrorList errors={data.errors} /> : null}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'ok' | 'warn';
}) {
  const ring =
    tone === 'ok'
      ? 'border-[#a7f3d0] bg-[#ecfdf5] text-[#065f46]'
      : tone === 'warn'
        ? 'border-[#fde68a] bg-[#fef9c3] text-[#854d0e]'
        : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)]';
  return (
    <div className={`rounded-lg border px-3 py-2 ${ring}`}>
      <div className="text-[10.5px] uppercase tracking-[0.1em] opacity-80">
        {label}
      </div>
      <div className="mt-1 text-[20px] font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ErrorList({
  errors,
}: {
  errors: Array<{ row: number; reason: string; raw?: string }>;
}) {
  return (
    <div className="rounded-lg border border-[#fde68a] bg-[#fef9c3] p-3 text-[12px]">
      <div className="mb-2 font-semibold text-[#854d0e]">
        {errors.length} row{errors.length === 1 ? '' : 's'} rejected
      </div>
      <ul className="max-h-[180px] space-y-1 overflow-y-auto font-mono text-[11.5px] text-[#854d0e]">
        {errors.slice(0, 100).map((e) => (
          <li key={`${e.row}-${e.reason}`}>
            Line {e.row}: {e.reason}
            {e.raw ? ` — ${e.raw}` : ''}
          </li>
        ))}
        {errors.length > 100 ? (
          <li className="opacity-70">… and {errors.length - 100} more</li>
        ) : null}
      </ul>
    </div>
  );
}
